import { useParams } from 'react-router'
import { FormState, useForm, UseFormRegister } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { changedRequestValidationSchema, IChangedRequestFormSchema } from './changed-request-validation-schema'
import { usePlatformUpdate } from '@reapitform/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { getFormStepQueryKey } from '../api/step/step-loader'
import { IFormStepData } from '../api/step'
import { getFormDocumentQueryKey } from '../api/document/document-loader'
import { IFormDocumentData } from '../api/document'

export interface IUseDialogSignatureResponse {
  formState: FormState<IChangedRequestFormSchema>
  register: UseFormRegister<IChangedRequestFormSchema>
  isSubmitLoading: boolean
  handleSubmitChangeRequest: () => (e?: React.BaseSyntheticEvent) => Promise<void>
  isValid: () => boolean
}

export const useDialogChangedRequest = ({
  onSuccess,
}: { onSuccess?: () => void } = {}): IUseDialogSignatureResponse => {
  const queryClient = useQueryClient()

  const { recipientId = 'mok-id-forms', stepsId = 0 } = useParams<string>()

  const form = useForm<IChangedRequestFormSchema>({
    resolver: yupResolver(changedRequestValidationSchema),
    defaultValues: {
      changesRequested: '',
      recipientId: recipientId,
      stepNumber: Number(stepsId),
    },
  })

  const { handleSubmit, register, formState, resetField } = form

  const [submitRequest, isSubmitLoading] = usePlatformUpdate<IChangedRequestFormSchema, IFormStepData>({
    path: '/api/recipient/ChangeRequest',
    method: 'POST',
    headers: {},
    isProtectedRoute: false,
  })

  const handleSubmitChangeRequest = () => {
    return handleSubmit(async (values: IChangedRequestFormSchema) => {
      const submitParams: IChangedRequestFormSchema = {
        ...values,
        recipientId: recipientId,
        stepNumber: Number(stepsId),
      }
      const result = await submitRequest(submitParams)

      if (result) {
        queryClient.setQueryData(
          getFormStepQueryKey(submitParams.recipientId, String(submitParams.stepNumber)),
          (): IFormStepData => result,
        )

        queryClient.setQueryData(
          getFormDocumentQueryKey(submitParams.recipientId),
          (oldData: IFormDocumentData): IFormDocumentData => ({
            ...oldData,
            changeRequests: [...new Set([...(oldData?.changeRequests || []), result.stepDisplayName])],
          }),
        )

        // reset the text field value when submitted
        resetField?.('changesRequested')

        // add handle close
        onSuccess?.()
      }
    })
  }

  const isValid = (): boolean => {
    return formState.isValid
  }

  return {
    isValid,
    formState,
    register,
    isSubmitLoading,
    handleSubmitChangeRequest,
  }
}
